import { TextField, TextFieldProps } from '@mui/material';
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import { Controller, useFormContext } from 'react-hook-form';

export type AutoCompleteOption = { [key: string]: any; id?: number } | string | number;

type SetRequired<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>;

type Props<T extends AutoCompleteOption> = {
  name: string;
  label: string;
  type?: 'text' | 'number';
  size?: TextFieldProps['size'];
  onCustomOnChange?: (nextOptionvalue: T | string | null) => void;
  /**
   * When true, stores only the `id` property from selected objects (for objects with an id field)
   * When false or undefined, stores the entire object (default behavior)
   */
  storeIdOnly?: boolean;
} & SetRequired<Partial<AutocompleteProps<T, false, false, boolean>>, 'options'> &
  Omit<TextFieldProps, 'variant'>;

export default function RHFAutocomplete<T extends AutoCompleteOption>({
  name,
  label,
  type = 'text',
  options,
  freeSolo,
  open,
  popupIcon,
  forcePopupIcon,
  clearOnBlur,
  autoHighlight,
  renderOption,
  getOptionLabel,
  filterOptions,
  isOptionEqualToValue,
  inputValue,
  selectOnFocus,
  onOpen,
  onClose,
  onInputChange,
  onCustomOnChange,
  storeIdOnly = false,
  size = 'medium',
  disabled,
  ...other
}: Props<T>) {
  const { control } = useFormContext();
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        // When storeIdOnly is true, find the object that matches the stored ID
        // If not found in options (e.g., async loading), keep the numeric value to preserve form state
        let displayValue: T | null = null;

        if (storeIdOnly && typeof value === 'number') {
          const foundOption = options.find(
            (opt) => typeof opt === 'object' && opt !== null && 'id' in opt && opt.id === value,
          );
          // If option found, use it; otherwise set to null to prevent errors but keep the form value
          displayValue = foundOption ? (foundOption as T) : null;
        } else {
          displayValue = value as T;
        }

        return (
          <Autocomplete<T, false, false, boolean>
            open={open}
            freeSolo={freeSolo}
            autoHighlight={autoHighlight}
            forcePopupIcon={forcePopupIcon}
            popupIcon={popupIcon}
            clearOnBlur={clearOnBlur}
            selectOnFocus={selectOnFocus}
            inputValue={inputValue}
            onOpen={onOpen}
            onClose={onClose}
            options={options}
            onInputChange={onInputChange}
            onChange={(_, selectedValue) => {
              // When storeIdOnly is true, extract and store only the ID
              const valueToStore =
                storeIdOnly && selectedValue && typeof selectedValue === 'object' && 'id' in selectedValue
                  ? selectedValue.id
                  : selectedValue;

              onCustomOnChange?.(selectedValue);
              onChange(valueToStore);
            }}
            getOptionLabel={
              getOptionLabel ? getOptionLabel : (option) => (typeof option === 'string' ? option : String(option))
            }
            value={displayValue}
            filterOptions={filterOptions}
            renderOption={renderOption}
            isOptionEqualToValue={isOptionEqualToValue}
            componentsProps={other.componentsProps}
            disabled={disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                {...other}
                name={name}
                label={label}
                variant="outlined"
                type={type}
                error={!!error}
                helperText={error ? error.message : ''}
                size={size}
                InputProps={{
                  ...params.InputProps,
                  ...other?.InputProps,
                }}
                fullWidth
              />
            )}
          />
        );
      }}
    />
  );
}
